import { create } from 'zustand';
import { DEFAULT_SETTINGS, type SystemSettings } from '@orbit-sync/shared-types';
import { api } from '../lib/api/index.js';

/**
 * The administrator's settings, as this browser knows them.
 *
 * Read by the forms and the upload queue to fail EARLY and to explain the
 * rule - the message-length counter, the password checklist, "that file is over
 * the limit" before a byte moves. They are never the gate: the server enforces
 * every one of them on its own.
 *
 * Starts at the defaults so nothing ever renders against an empty object, then
 * loads once at startup. Changes arrive live over `system.settings` while
 * signed in; a signed-out page (the registration form) gets them on load.
 */
interface SettingsState {
  settings: SystemSettings;
  status: 'idle' | 'loading' | 'ready' | 'error';

  load: () => Promise<void>;
  /** Replaces the whole object: what the server sends is always complete. */
  apply: (settings: SystemSettings) => void;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  status: 'idle',

  async load(): Promise<void> {
    if (get().status === 'loading') {
      return;
    }

    set({ status: 'loading' });

    try {
      set({ settings: await api.settings.getSettings(), status: 'ready' });
    } catch {
      // The defaults stay in place. Worst case the client is more permissive
      // than the server for a while - and the server says no on its own.
      set({ status: 'error' });
    }
  },

  apply(settings: SystemSettings): void {
    set({ settings, status: 'ready' });
  },
}));

/** Current settings, for code outside React - the upload queue, mainly. */
export function currentSettings(): SystemSettings {
  return useSettingsStore.getState().settings;
}
